<template>
  <ApexChart v-if="chartData && chartData.series" element="AuxiliaryEngineUsage" :chartOption="chartData"
    :series="chartData.series" />
</template>

<script>
import moment from 'moment';

export default {
  name: 'AuxiliaryEngineUsage',
  props: ['series'],
  computed: {
    chartData() {
      if (!this.series) return null;
      const isMobile = window.innerWidth <= 991;

      // 1. Ekstraksi data, ubah data kosong menjadi null
      const mapData = (path) => this.series.map((data) => {
        const value = path.split('.').reduce((o, i) => (o ? o[i] : undefined), data);
        return Number.isFinite(Number(value)) ? Number(Number(value).toFixed(2)) : null;
      });

      const MappedAe1FuelCons = mapData('aeData.AE1.fuelConsumption');
      const MappedAe2FuelCons = mapData('aeData.AE2.fuelConsumption');
      const MappedAe3FuelCons = mapData('aeData.AE3.fuelConsumption');
      const MappedAe1RunningSeconds = mapData('aeData.AE1.runningSeconds');
      const MappedAe2RunningSeconds = mapData('aeData.AE2.runningSeconds');
      const MappedAe3RunningSeconds = mapData('aeData.AE3.runningSeconds');

      // 2. Terapkan interpolasi
      const InterpolatedAe1FuelCons = this.interpolateNulls(MappedAe1FuelCons);
      const InterpolatedAe2FuelCons = this.interpolateNulls(MappedAe2FuelCons);
      const InterpolatedAe3FuelCons = this.interpolateNulls(MappedAe3FuelCons);
      const InterpolatedAe1RunningSeconds = this.interpolateNulls(MappedAe1RunningSeconds);
      const InterpolatedAe2RunningSeconds = this.interpolateNulls(MappedAe2RunningSeconds);
      const InterpolatedAe3RunningSeconds = this.interpolateNulls(MappedAe3RunningSeconds);

      const safeMax = (arr, fallback = 10) => {
        const vals = arr.map((v) => (typeof v === 'string' ? Number(v) : v)).filter((v) => Number.isFinite(v));
        return vals.length ? Math.max(...vals) : fallback;
      };

      let maxAEFuelCons = safeMax([...InterpolatedAe1FuelCons, ...InterpolatedAe2FuelCons, ...InterpolatedAe3FuelCons]);
      maxAEFuelCons += (maxAEFuelCons * 0.10);

      let maxAERunningSeconds = safeMax([...InterpolatedAe1RunningSeconds, ...InterpolatedAe2RunningSeconds, ...InterpolatedAe3RunningSeconds]);
      maxAERunningSeconds += (maxAERunningSeconds * 0.10);

      const seriesNames = {
        ae1Fuel: 'AE 1 Fuel Consumption (L)',
        ae2Fuel: 'AE 2 Fuel Consumption (L)',
        ae3Fuel: 'AE 3 Fuel Consumption (L)',
        ae1Rh: 'AE 1 Running Seconds',
        ae2Rh: 'AE 2 Running Seconds',
        ae3Rh: 'AE 3 Running Seconds',
      };

      const seriesData = [
        { name: seriesNames.ae1Fuel, data: MappedAe1FuelCons, color: '#FF0000' },
        { name: `${seriesNames.ae1Fuel} (Interpolated)`, data: InterpolatedAe1FuelCons, color: 'rgba(255, 0, 0, 0.4)' },
        { name: seriesNames.ae2Fuel, data: MappedAe2FuelCons, color: '#00FF00' },
        { name: `${seriesNames.ae2Fuel} (Interpolated)`, data: InterpolatedAe2FuelCons, color: 'rgba(0, 255, 0, 0.4)' },
        { name: seriesNames.ae3Fuel, data: MappedAe3FuelCons, color: '#0000FF' },
        { name: `${seriesNames.ae3Fuel} (Interpolated)`, data: InterpolatedAe3FuelCons, color: 'rgba(0, 0, 255, 0.4)' },
        { name: seriesNames.ae1Rh, data: MappedAe1RunningSeconds, color: '#FFFF00' },
        { name: `${seriesNames.ae1Rh} (Interpolated)`, data: InterpolatedAe1RunningSeconds, color: 'rgba(255, 255, 0, 0.4)' },
        { name: seriesNames.ae2Rh, data: MappedAe2RunningSeconds, color: '#FF00FF' },
        { name: `${seriesNames.ae2Rh} (Interpolated)`, data: InterpolatedAe2RunningSeconds, color: 'rgba(255, 0, 255, 0.4)' },
        { name: seriesNames.ae3Rh, data: MappedAe3RunningSeconds, color: '#00FFFF' },
        { name: `${seriesNames.ae3Rh} (Interpolated)`, data: InterpolatedAe3RunningSeconds, color: 'rgba(0, 255, 255, 0.4)' },
      ];

      const legendFillColors = seriesData.map((s) => (s.name.includes('(Interpolated)') ? 'transparent' : s.color));

      return {
        series: seriesData,
        chart: {
          height: 600,
          width: '100%',
          type: 'line',
          stacked: false,
          animations: {
            enabled: false,
          },
          events: {
            legendClick(chartContext, seriesIndex) {
              const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
              const interpolatedName = `${seriesName} (Interpolated)`;
              chartContext.toggleSeries(interpolatedName);
            },
          },
          zoom: {
            enabled: true,
            allowMouseWheelZoom: false,
          },
        },
        markers: {
          size: 4,
        },
        stroke: {
          width: [3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2],
          dashArray: [0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8],
        },
        legend: {
          position: 'bottom',
          formatter(seriesName) {
            if (seriesName.includes('(Interpolated)')) {
              return '';
            }
            return seriesName;
          },
          markers: {
            fillColors: legendFillColors,
          },
        },
        tooltip: {
          shared: true,
          custom({
            series, dataPointIndex, w,
          }) {
            const header = w.globals.categoryLabels[dataPointIndex];
            let tooltipContent = '';

            for (let i = 0; i < series.length; i++) {
              if (!w.globals.seriesNames[i].includes('(Interpolated)')) {
                const seriesValue = series[i][dataPointIndex];
                if (seriesValue !== null && typeof seriesValue !== 'undefined') {
                  const seriesColor = w.globals.colors[i];
                  const seriesName = w.globals.seriesNames[i];
                  tooltipContent += `
                    <div class="apexcharts-tooltip-series-group" style="order: ${i}; display: flex;">
                      <span class="apexcharts-tooltip-marker" style="background-color: ${seriesColor};"></span>
                      <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                        <div class="apexcharts-tooltip-y-group">
                          <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                          <span class="apexcharts-tooltip-text-y-value">${seriesValue}</span>
                        </div>
                      </div>
                    </div>`;
                }
              }
            }
            if (tooltipContent === '') return '';
            return `
              <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                ${header}
              </div>
              <div class="apexcharts-tooltip-series-group-wrapper" style="display: flex; flex-direction: column;">
                ${tooltipContent}
              </div>`;
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            formatter(val) {
              return moment(val).format('DD-MM-YYYY HH:mm');
            },
          },
          categories: this.series.map((data) => moment(data.timestamp * 1000).format('DD MMMM YYYY HH:mm')),
          tickAmount: isMobile ? 3 : undefined,
        },
        yaxis: [
          // Fuel Consumption Axis
          {
            seriesName: seriesNames.ae1Fuel,
            max: maxAEFuelCons,
            title: { text: 'Fuel Consumption (L)' },
            show: !isMobile,
          },
          {
            seriesName: seriesNames.ae1Fuel, max: maxAEFuelCons, show: false,
          }, // Interpolated
          {
            seriesName: seriesNames.ae2Fuel, max: maxAEFuelCons, show: false,
          },
          {
            seriesName: seriesNames.ae2Fuel, max: maxAEFuelCons, show: false,
          }, // Interpolated
          {
            seriesName: seriesNames.ae3Fuel, max: maxAEFuelCons, show: false,
          },
          {
            seriesName: seriesNames.ae3Fuel, max: maxAEFuelCons, show: false,
          }, // Interpolated
          // Running Seconds Axis
          {
            seriesName: seriesNames.ae1Rh,
            opposite: true,
            max: maxAERunningSeconds,
            title: { text: 'Running Seconds' },
            show: !isMobile,
          },
          {
            seriesName: seriesNames.ae1Rh, opposite: true, max: maxAERunningSeconds, show: false,
          }, // Interpolated
          {
            seriesName: seriesNames.ae2Rh, opposite: true, max: maxAERunningSeconds, show: false,
          },
          {
            seriesName: seriesNames.ae2Rh, opposite: true, max: maxAERunningSeconds, show: false,
          }, // Interpolated
          {
            seriesName: seriesNames.ae3Rh, opposite: true, max: maxAERunningSeconds, show: false,
          },
          {
            seriesName: seriesNames.ae3Rh, opposite: true, max: maxAERunningSeconds, show: false,
          }, // Interpolated
        ],
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }

            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>

<style scoped></style>
